import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Play,
  X,
  FilterX,
  ChevronLeft,
  ChevronRight,
  Code2,
  Lock,
  Unlock,
  Tag,
  Home,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import pb from '../lib/pocketbaseClient';

const CATEGORIES = [
  "Construction",
  "Architecture",
  "Interior",
  "EMI Calculator",
  "Building Materials",
  "Area Calculator",
  "Cost Estimator",
  "Vastu Calculator",
  "Civil Engineering",
];

const ITEMS_PER_PAGE = 6;

const StatusBadge = ({ variant = "default", children }) => {
  const styles = {
    default: "bg-slate-100 text-slate-700",
    free: "bg-green-100 text-green-800",
    premium: "bg-purple-100 text-purple-800",
    published: "bg-green-100 text-green-800",
    draft: "bg-gray-100 text-gray-600",
    featured: "bg-amber-100 text-amber-800",
    homepage: "bg-indigo-100 text-indigo-800",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[variant] || styles.default}`}>
      {children}
    </span>
  );
};

const Calculators = () => {
  const [calculators, setCalculators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [previewCalc, setPreviewCalc] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: "",
    slug: "",
    shortDescription: "",
    fullDescription: "",
    featuredImage: "",
    category: "Construction",
    htmlCode: "",
    cssCode: "",
    jsCode: "",
    freeOrPremium: "free",
    featured: false,
    showOnHomepage: false,
    status: "draft",
    tags: [],
    seoTitle: "",
    seoDescription: "",
    subscriptionRequired: false,
  };
  const [formData, setFormData] = useState(emptyForm);

  // ✅ Fetch calculators from PocketBase
  const fetchCalculators = async (showToast = true) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📡 Fetching calculators from PocketBase...');
      console.log('🔐 Auth valid:', pb.authStore?.isValid || false);
      console.log('📚 Collection: calculators');
      
      // Check if pb is available
      if (!pb) {
        throw new Error('PocketBase client not initialized');
      }
      
      // Try to get records
      const records = await pb.collection('calculators').getFullList({
        sort: '-created',
        $autoCancel: false,
      });
      
      console.log('✅ Fetched records:', records.length);
      setCalculators(records);
      
      if (records.length === 0 && showToast) {
        toast.info('No calculators found. Add your first calculator!');
      } else if (records.length > 0 && showToast) {
        toast.success(`Loaded ${records.length} calculators`);
      }
    } catch (error) {
      console.error('❌ Failed to fetch calculators:', error);
      
      let errorMsg = error.message || 'Unknown error';
      let errorTitle = 'Failed to load calculators';
      
      if (error.status === 404) {
        errorMsg = 'Calculators collection not found. Please create it in PocketBase admin.';
        errorTitle = 'Collection Not Found';
      } else if (error.status === 403) {
        errorMsg = 'Permission denied. Please check API rules in PocketBase admin.';
        errorTitle = 'Permission Denied';
      } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
        errorMsg = 'PocketBase server not reachable. Please ensure it is running on port 8090.';
        errorTitle = 'Connection Error';
      }
      
      setError({ message: errorMsg, title: errorTitle });
      if (showToast) {
        toast.error(errorTitle + ': ' + errorMsg);
      }
      setCalculators([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Initial fetch - Runs on component mount
  useEffect(() => {
    fetchCalculators();
  }, []);

  // ✅ Retry fetch
  const handleRetry = () => {
    fetchCalculators(true);
  };

  // Stats
  const stats = useMemo(() => {
    const total = calculators.length;
    const free = calculators.filter(c => c.freeOrPremium === "free" || c.freeOrPremium === "Free").length;
    const premium = calculators.filter(c => c.freeOrPremium === "premium" || c.freeOrPremium === "Premium").length;
    const homepage = calculators.filter(c => c.showOnHomepage === true).length;
    return { total, free, premium, homepage };
  }, [calculators]);

  // Filtering
  const filteredCalculators = useMemo(() => {
    return calculators.filter(calc => {
      const matchesSearch = calc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           calc.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === "All" || calc.category === filterCategory;
      const matchesType = filterType === "All" || (calc.freeOrPremium || 'free').toLowerCase() === filterType.toLowerCase();
      const matchesStatus = filterStatus === "All" || (calc.status || 'draft').toLowerCase() === filterStatus.toLowerCase();
      return matchesSearch && matchesCategory && matchesType && matchesStatus;
    });
  }, [calculators, searchQuery, filterCategory, filterType, filterStatus]);

  const totalPages = Math.ceil(filteredCalculators.length / ITEMS_PER_PAGE);
  const paginatedCalculators = filteredCalculators.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const resetFilters = () => {
    setSearchQuery("");
    setFilterCategory("All");
    setFilterType("All");
    setFilterStatus("All");
    setCurrentPage(1);
  };

  // Generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // ✅ Save to PocketBase
  const handleSaveCalculator = async (data, id) => {
    setSaving(true);
    try {
      const slug = data.slug || generateSlug(data.name);
      
      console.log('💾 Saving calculator:', { data, id, slug });
      
      if (id) {
        await pb.collection('calculators').update(id, {
          ...data,
          slug,
        });
        toast.success('✅ Calculator updated successfully');
      } else {
        await pb.collection('calculators').create({
          ...data,
          slug,
        });
        toast.success('✅ Calculator created successfully');
      }
      
      await fetchCalculators(false);
      setView('list');
    } catch (error) {
      console.error('❌ Save error:', error);
      toast.error(error.message || 'Failed to save calculator');
    } finally {
      setSaving(false);
    }
  };

  // ✅ Delete
  const handleDelete = async (id) => {
    try {
      await pb.collection('calculators').delete(id);
      toast.success('✅ Calculator deleted');
      await fetchCalculators(false);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Failed to delete calculator');
    }
  };

  const handleEditClick = (calc) => {
    setFormData({ ...calc });
    setEditingId(calc.id);
    setView('edit');
  };

  const handleAddClick = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setView('add');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSaveCalculator(formData, editingId);
  };

  const previewHtml = useMemo(() => {
    return `<!DOCTYPE html><html><head><style>${formData.cssCode}</style></head><body>${formData.htmlCode}<script>${formData.jsCode}<\/script></body></html>`;
  }, [formData.cssCode, formData.htmlCode, formData.jsCode]);

  // ✅ Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-white rounded-2xl border border-slate-200 p-8">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Code2 className="w-10 h-10 text-red-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">{error.title || 'Error'}</h3>
        <p className="text-slate-500 text-center max-w-md mb-6">{error.message}</p>
        <div className="flex gap-3">
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-50 transition"
          >
            Reload Page
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-4">
          PocketBase URL: https://housedesigns99.com | Collection: calculators
        </p>
      </div>
    );
  }

  // ✅ Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Calculator Management</h1>
          <p className="text-slate-500 text-sm">
            Build custom calculators with HTML, CSS, and JS
            <span className="ml-2 text-green-600 font-medium">
              ✅ {calculators.length} calculators loaded
            </span>
          </p>
        </div>
        <button onClick={handleAddClick} className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition">
          <Plus className="w-5 h-5" /> Add New Calculator
        </button>
      </div>

      {/* List View */}
      {view === "list" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
              <div className="p-3 rounded-xl bg-blue-100 text-blue-700"><Code2 className="w-6 h-6" /></div>
              <div><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-slate-500">Total Calculators</p></div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
              <div className="p-3 rounded-xl bg-green-100 text-green-700"><Unlock className="w-6 h-6" /></div>
              <div><p className="text-2xl font-bold">{stats.free}</p><p className="text-sm text-slate-500">Free</p></div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
              <div className="p-3 rounded-xl bg-purple-100 text-purple-700"><Lock className="w-6 h-6" /></div>
              <div><p className="text-2xl font-bold">{stats.premium}</p><p className="text-sm text-slate-500">Premium</p></div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
              <div className="p-3 rounded-xl bg-indigo-100 text-indigo-700"><Home className="w-6 h-6" /></div>
              <div><p className="text-2xl font-bold">{stats.homepage}</p><p className="text-sm text-slate-500">Homepage Featured</p></div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search calculators..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <select 
                  value={filterCategory} 
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="All">All Categories</option>
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <select 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="All">All Types</option>
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                </select>
                <select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="All">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
                <button onClick={resetFilters} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition" title="Reset">
                  <FilterX className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Calculator Cards */}
          {calculators.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
              <Code2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No calculators found</h3>
              <p className="text-slate-500 mb-4">Add your first calculator to get started.</p>
              <button onClick={handleAddClick} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition">
                <Plus className="w-4 h-4 inline mr-2" /> Add Calculator
              </button>
            </div>
          ) : filteredCalculators.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
              <Search className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No matching calculators</h3>
              <p className="text-slate-500 mb-4">No calculators match your current filters.</p>
              <button onClick={resetFilters} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 transition">Clear Filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedCalculators.map(calc => (
                <motion.div
                  layout
                  key={calc.id}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all group"
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold truncate mr-2">{calc.name}</h3>
                      <StatusBadge variant={calc.freeOrPremium === 'premium' ? 'premium' : 'free'}>
                        {calc.freeOrPremium || 'free'}
                      </StatusBadge>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {calc.featured && <StatusBadge variant="featured">⭐ Featured</StatusBadge>}
                      {calc.showOnHomepage && <StatusBadge variant="homepage">Homepage</StatusBadge>}
                      <StatusBadge variant={calc.status === 'published' ? 'published' : 'draft'}>
                        {calc.status || 'draft'}
                      </StatusBadge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <Tag className="w-3.5 h-3.5" /> {calc.category || 'Uncategorized'}
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => setPreviewCalc(calc)} className="inline-flex items-center gap-1 px-2 py-1 text-sm hover:bg-slate-100 rounded-lg transition">
                        <Play className="w-4 h-4" /> Preview
                      </button>
                      <div className="flex gap-1">
                        <button onClick={() => handleEditClick(calc)} className="p-1 hover:bg-slate-100 rounded-lg transition">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteConfirm(calc)} className="p-1 hover:bg-red-100 rounded-lg transition text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`px-3 py-1 rounded-lg transition ${currentPage === i + 1 ? 'bg-indigo-600 text-white' : 'border border-slate-200 hover:bg-slate-50'}`}>
                  {i + 1}
                </button>
              ))}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Form */}
      {(view === "add" || view === "edit") && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-2xl p-6"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{view === "add" ? "New Calculator" : "Edit Calculator"}</h2>
            <button onClick={() => setView("list")} className="inline-flex items-center gap-2 px-4 py-2 hover:bg-slate-100 rounded-lg transition">
              <X className="w-5 h-5" /> Back
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-2">
            {["General", "HTML", "CSS", "JavaScript", "Preview", "SEO"].map((tab, index) => (
              <button
                key={tab}
                className={`px-4 py-2 text-sm font-medium transition ${
                  index === 0 
                    ? 'bg-indigo-100 text-indigo-700 rounded-lg' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                onClick={(e) => {
                  const parent = e.currentTarget.parentElement;
                  parent.querySelectorAll('button').forEach(btn => {
                    btn.classList.remove('bg-indigo-100', 'text-indigo-700', 'rounded-lg');
                    btn.classList.add('text-slate-500');
                  });
                  e.currentTarget.classList.remove('text-slate-500');
                  e.currentTarget.classList.add('bg-indigo-100', 'text-indigo-700', 'rounded-lg');
                  
                  const tabId = tab.toLowerCase();
                  document.querySelectorAll('.tab-content').forEach(el => el.classList.add('hidden'));
                  const target = document.getElementById(`tab-${tabId}`);
                  if (target) target.classList.remove('hidden');
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* General Tab */}
            <div id="tab-general" className="tab-content">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <input 
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      value={formData.name} 
                      onChange={(e) => handleFormChange("name", e.target.value)} 
                      required 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Slug (auto-generated)</label>
                    <input 
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      value={formData.slug} 
                      onChange={(e) => handleFormChange("slug", e.target.value)} 
                      placeholder="auto-generated" 
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Short Description</label>
                    <input 
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      value={formData.shortDescription} 
                      onChange={(e) => handleFormChange("shortDescription", e.target.value)} 
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Full Description (HTML)</label>
                    <textarea rows={4} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" value={formData.fullDescription} onChange={(e) => handleFormChange("fullDescription", e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select 
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none bg-white"
                      value={formData.category} 
                      onChange={(e) => handleFormChange("category", e.target.value)}
                    >
                      {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Featured Image URL</label>
                    <input 
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      value={formData.featuredImage} 
                      onChange={(e) => handleFormChange("featuredImage", e.target.value)} 
                    />
                  </div>
                  <div className="flex flex-wrap gap-4 items-center">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={formData.featured} onChange={(e) => handleFormChange("featured", e.target.checked)} className="rounded" />
                      <span>Featured</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={formData.showOnHomepage} onChange={(e) => handleFormChange("showOnHomepage", e.target.checked)} className="rounded" />
                      <span>Show on Homepage</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={formData.subscriptionRequired} onChange={(e) => handleFormChange("subscriptionRequired", e.target.checked)} className="rounded" />
                      <span>Subscription Required</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="freeOrPremium" value="free" checked={formData.freeOrPremium === "free"} onChange={() => handleFormChange("freeOrPremium", "free")} />
                      <span>Free</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="freeOrPremium" value="premium" checked={formData.freeOrPremium === "premium"} onChange={() => handleFormChange("freeOrPremium", "premium")} />
                      <span>Premium</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select 
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none bg-white"
                      value={formData.status} 
                      onChange={(e) => handleFormChange("status", e.target.value)}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                    <input 
                      type="text"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      value={Array.isArray(formData.tags) ? formData.tags.join(", ") : ""}
                      onChange={(e) => handleFormChange("tags", e.target.value.split(",").map(t => t.trim()).filter(Boolean))}
                      placeholder="e.g. construction, cement"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* HTML Tab */}
            <div id="tab-html" className="tab-content hidden">
              <label className="block text-sm font-medium mb-1">HTML Code</label>
              <textarea 
                rows={12}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none font-mono text-sm"
                value={formData.htmlCode} 
                onChange={(e) => handleFormChange("htmlCode", e.target.value)}
                placeholder="<!-- HTML code for your calculator -->"
              />
            </div>

            {/* CSS Tab */}
            <div id="tab-css" className="tab-content hidden">
              <label className="block text-sm font-medium mb-1">CSS Code</label>
              <textarea 
                rows={12}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none font-mono text-sm"
                value={formData.cssCode} 
                onChange={(e) => handleFormChange("cssCode", e.target.value)}
                placeholder="/* CSS styles for your calculator */"
              />
            </div>

            {/* JavaScript Tab */}
            <div id="tab-javascript" className="tab-content hidden">
              <label className="block text-sm font-medium mb-1">JavaScript Code</label>
              <textarea 
                rows={12}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none font-mono text-sm"
                value={formData.jsCode} 
                onChange={(e) => handleFormChange("jsCode", e.target.value)}
                placeholder="// JavaScript logic for your calculator"
              />
            </div>

            {/* Preview Tab */}
            <div id="tab-preview" className="tab-content hidden">
              <p className="text-sm text-slate-500 mb-2">Real-time preview of your calculator</p>
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <iframe
                  key={previewHtml}
                  srcDoc={previewHtml}
                  title="preview"
                  className="w-full h-96"
                  sandbox="allow-scripts"
                />
              </div>
            </div>

            {/* SEO Tab */}
            <div id="tab-seo" className="tab-content hidden">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">SEO Title</label>
                  <input 
                    type="text"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                    value={formData.seoTitle} 
                    onChange={(e) => handleFormChange("seoTitle", e.target.value)} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">SEO Description</label>
                  <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 outline-none" value={formData.seoDescription} onChange={(e) => handleFormChange("seoDescription", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t mt-6">
              <button type="button" onClick={() => setView("list")} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : null}
                {view === "add" ? "Create Calculator" : "Update Calculator"}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center" onClick={() => setDeleteConfirm(null)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md p-6 text-center shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Delete Calculator?</h3>
              <p className="text-slate-500 mb-6">"{deleteConfirm.name}" will be permanently removed.</p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition">Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition">Delete</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewCalc && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewCalc(null)}>
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-bold">{previewCalc.name}</h2>
                <button onClick={() => setPreviewCalc(null)} className="p-2 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <iframe
                key={previewCalc.id}
                srcDoc={`<!DOCTYPE html><html><head><style>${previewCalc.cssCode || ''}</style></head><body>${previewCalc.htmlCode || ''}<script>${previewCalc.jsCode || ''}<\/script></body></html>`}
                title="preview"
                className="w-full h-[70vh]"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Calculators;